// "Les Chambres" — the directory overlay that IS the map "link" to per-avatar rooms. Toggled (key C / a
// HUD trigger emits 'toggle-chambres'); fetches the public /api/rooms; lists « Ma chambre » + « la chambre
// de X » with a vibe/occupancy line; tapping a row enters that room (emits 'room-enter' → game.ts). Built
// fresh on open (no persistent state), backdrop-tap to close. Same overlay pattern as LogScene/Minimap.
import 'phaser'
import GlobalInfo from '../GlobalInfo'
import ServerLink from '../net/ServerLink'
import { SceneName } from './enums/SceneNames'

type RoomEntry = { roomId: string; ownerName: string; mine?: boolean; occupants: string[]; propCount: number; decorCount: number; recentVisits: number; ownerHome: boolean; vibe: number }

export default class ChambresScene extends Phaser.Scene {
    private open = false
    private layer?: Phaser.GameObjects.Container

    constructor() { super(SceneName.Chambres) }

    create(): void {
        GlobalInfo.on('toggle-chambres', this.toggle, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => GlobalInfo.off('toggle-chambres', this.toggle, this))
    }

    private toggle = (): void => { if (this.open) this.close(); else void this.openPanel() }

    private close(): void { this.open = false; this.layer?.destroy(); this.layer = undefined }

    private async openPanel(): Promise<void> {
        this.open = true
        let rooms: RoomEntry[] = []
        // Carry the player's token (if linked) so the server flags « Ma chambre » (mine = your claimed avatar).
        try { rooms = (await (await fetch(ServerLink.httpBase + '/api/rooms', { headers: ServerLink.authHeaders() })).json()).rooms || [] } catch { /* offline */ }
        if (!this.open) return // closed while fetching
        this.layer?.destroy()
        const W = this.scale.width, H = this.scale.height
        const c = this.add.container(0, 0).setScrollFactor(0).setDepth(4000)
        this.layer = c
        const bg = this.add.rectangle(0, 0, W, H, 0x0a0e16, 0.82).setOrigin(0).setInteractive()
        bg.on('pointerdown', () => this.close())
        c.add(bg)
        c.add(this.add.text(W / 2, 22, '🚪 Les Chambres', { fontFamily: 'sans-serif', fontSize: '18px', fontStyle: 'bold', color: '#ffffff' }).setOrigin(0.5).setResolution(2))
        const panelW = Math.min(360, W - 24)
        const px = (W - panelW) / 2
        const maxVibe = rooms.reduce((m, r) => Math.max(m, r.vibe), 0)
        rooms.sort((a, b) => (b.mine ? 1 : 0) - (a.mine ? 1 : 0)) // « Ma chambre » first (server vibe-sorts the rest)
        let y = 52
        for (const room of rooms.slice(0, 14)) {
            const hot = room.vibe > 0 && room.vibe === maxVibe // 🔥 most-alive room of the class
            const label = `${hot ? '🔥 ' : ''}${room.mine ? '⭐ Ma chambre' : `la chambre de ${room.ownerName}`}`
            // Story line: LIVE now → else recent async activity → else the owner's rhythm (so a room never reads "dead").
            let sub: string
            if (room.occupants.length) sub = `🟢 ${room.occupants.length} pote${room.occupants.length > 1 ? 's' : ''} ici maintenant`
            else {
                const bits: string[] = []
                if (room.recentVisits > 0) bits.push(`👋 ${room.recentVisits} passage${room.recentVisits > 1 ? 's' : ''} récent${room.recentVisits > 1 ? 's' : ''}`)
                bits.push(room.ownerHome ? '🟢 chez soi' : 'rentre à 16h25')
                if (room.decorCount > 0) bits.push(`${room.decorCount}🎨`)
                else if (room.propCount === 0) bits.push('encore vide')
                sub = bits.join('  ·  ')
            }
            const row = this.add.container(px, y)
            const box = this.add.rectangle(0, 0, panelW, 38, room.mine ? 0x2b4c7e : 0x1b2230, 1).setOrigin(0)
                .setStrokeStyle(1, room.occupants.length ? 0x48bb78 : 0x2c3647).setInteractive({ useHandCursor: true })
            box.on('pointerdown', () => { GlobalInfo.emit('room-enter', { roomId: room.roomId, ownerName: room.ownerName, mine: !!room.mine, recentVisits: room.recentVisits }); this.close() })
            row.add(box)
            row.add(this.add.text(10, 5, label, { fontFamily: 'sans-serif', fontSize: '14px', fontStyle: 'bold', color: '#eef3ff' }).setResolution(2))
            row.add(this.add.text(10, 22, sub, { fontFamily: 'sans-serif', fontSize: '11px', color: room.occupants.length ? '#9ae6b4' : '#9fb3c8' }).setResolution(2))
            c.add(row)
            y += 44
        }
        if (!rooms.length) c.add(this.add.text(W / 2, H / 2, 'Aucune chambre (serveur hors-ligne ?)', { fontFamily: 'sans-serif', fontSize: '13px', color: '#9fb3c8' }).setOrigin(0.5))
        c.add(this.add.text(W / 2, H - 18, 'tape ailleurs pour fermer', { fontFamily: 'sans-serif', fontSize: '11px', color: '#6b7a8d' }).setOrigin(0.5))
    }
}
